import { Component, ElementRef, OnInit, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime } from 'rxjs/operators';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { ProductTask } from 'src/app/models/product_task.model';
import { User } from 'src/app/models/user.model';
import { PermissionsService } from 'src/app/services';
import { ApiService } from 'src/app/services/api.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { UserService } from 'src/app/services/user.service';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { NgClass, NgStyle } from '@angular/common';
import { MatChip } from '@angular/material/chips';
import { ProductTaskTypeImagePipe } from '../../../pipes/product-task-type-image.pipe';
import { PotentialClient } from 'src/app/models/potential-client.interface';

@Component({
  selector: 'app-general-search-dialog',
  templateUrl: './general-search-dialog.component.html',
  styleUrls: ['./general-search-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FormsModule, ReactiveFormsModule, MatIcon, MatInput, NgClass, NgStyle, MatChip, ProductTaskTypeImagePipe],
})
export class GeneralSearchDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<GeneralSearchDialogComponent>>(MatDialogRef);
  data = inject(MAT_DIALOG_DATA);
  protected fb = inject(UntypedFormBuilder);
  private api = inject(ApiService);
  protected router = inject(Router);
  private employee = inject(EmployeeService);
  private userService = inject(UserService);
  private route = inject(ActivatedRoute);
  private permissions = inject(PermissionsService);

  form: UntypedFormGroup;
  suggestions: any[] = [];
  currentIndex = -1;
  me: User = null as any;
  intranetRoutes = [
    { name: 'Calendario', icon: 'calendar_today', model: 'intranet', route: ['/employees', 'activity', 'calendar'] },
    { name: 'Control horario', icon: 'schedule', model: 'intranet', route: ['/employees', 'activity', 'clock-registers'] },
    { name: 'Registro de trabajo', icon: 'history', model: 'intranet', route: ['/employees', 'activity', 'history'] },
  ];

  @ViewChild('suggestionsContainer') suggestionsContainer!: ElementRef;

  constructor() {
    this.form = this.fb.group({
      search: [''],
      intranetRoutes: [this.intranetRoutes.map(route => route.name)],
    });
  }

  ngOnInit(): void {
    this.listenForm();
    this.customizeRoutes();
  }

  listenForm() {
    this.form.valueChanges.pipe(debounceTime(500)).subscribe((data: any) => {
      if (data.search && data.search.length >= 3) {
        this.api.generalSearch(data).subscribe((response: any) => {
          // Filtrem si s'està compartint pantalla
          if (this.employee.isMeetingOngoing()) {
            response = response.filter(
              (row: any) =>
                (row.model != 'company_product' && row.model != 'task' && row.model != 'potential_client') ||
                (row.model == 'company_product' && this.employee.isMeetingOngoing(row.id)) ||
                (row.model == 'task' && this.employee.isMeetingOngoing(row.company_product_phase.company_product_id))
            );
          }
          this.suggestions = response;
        });
      } else {
        this.suggestions = [];
      }
    });
  }

  customizeRoutes() {
    this.me = this.userService.getCurrentUser();
    let newRoutes: { name: string; icon: string; model: string; route: string[] }[] = [];
    if (this.permissions.isDepartment('direction')) {
      newRoutes = [
        { name: 'Planificación y hoja de ruta', icon: 'view_timeline', model: 'intranet', route: ['/employees', 'development', 'roadmap'] },
        { name: 'Asignación de productos', icon: 'diversity_2', model: 'intranet', route: ['/employees', 'development', 'assignment'] },
      ];
    }
    this.intranetRoutes = [...this.intranetRoutes, ...newRoutes];
    this.form.patchValue({ intranetRoutes: this.intranetRoutes.map(route => route.name) });
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = Math.min(this.currentIndex + 1, this.suggestions.length - 1);
      this.scrollSuggestionIntoView(nextIndex);
      this.currentIndex = nextIndex;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prevIndex = Math.max(this.currentIndex - 1, 0);
      this.scrollSuggestionIntoView(prevIndex);
      this.currentIndex = prevIndex;
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (this.currentIndex >= 0 && this.currentIndex < this.suggestions.length) {
        const suggestion = this.suggestions[this.currentIndex];
        if (suggestion.model === 'company_product') {
          this.goToCompanyProduct(suggestion);
        } else if (suggestion.model === 'task') {
          this.goToTask(suggestion);
        } else if (suggestion.model === 'intranet') {
          this.goToIntranet(suggestion);
        } else if (suggestion.model === 'wiki') {
          this.goToWiki(suggestion);
        } else if (suggestion.model === 'potential_client') {
          this.goToPotentialClient(suggestion);
        }
      }
    }

    // Enfoca el contenedor de sugerencias después de manejar el evento de teclado
    if (this.suggestionsContainer && this.suggestionsContainer.nativeElement && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      this.suggestionsContainer.nativeElement.focus();
    }
  }

  scrollSuggestionIntoView(index: number) {
    if (this.suggestionsContainer && this.suggestionsContainer.nativeElement) {
      const suggestionElements = this.suggestionsContainer.nativeElement.querySelectorAll('.suggestion');
      if (suggestionElements.length > 0 && index >= 0 && index < suggestionElements.length) {
        const selectedSuggestion = suggestionElements[index];
        selectedSuggestion.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  goToCompanyProduct(company_product: CompanyProduct) {
    this.router.navigate(['/employees', 'development', 'company-products', company_product.id]);
    this.dialogRef.close();
  }

  goToTask(task: ProductTask) {
    const targetRoute = task.product_task_boards.length
      ? [
          '/employees',
          'development',
          'company-products',
          task.company_product_phase.company_product_id,
          'phases',
          task.company_product_phase_id,
          'boards',
          task.product_task_boards[0].company_product_phase_board_col.company_product_phase_board_id,
        ]
      : [
          '/employees',
          'development',
          'company-products',
          task.company_product_phase.company_product_id,
          'phases',
          task.company_product_phase_id,
          'backlog',
        ];

    const currentParams = { ...this.route.snapshot.queryParams };
    const mergedParams = { ...currentParams, selectedIssue: task.code };

    this.router.navigateByUrl('/').then((): void => {
      this.router.navigate(targetRoute, { queryParams: mergedParams });
    });

    this.dialogRef.close();
  }

  goToIntranet(route: any) {
    route = this.intranetRoutes.find(r => r.name === route.name);
    if (route) {
      this.router.navigate(route.route);
      this.dialogRef.close();
    }
  }

  goToWiki(route: any) {
    this.router.navigate(['/employees', 'wiki', route.wiki_node_id, 'article', route.id]);
    this.dialogRef.close();
  }

  goToPotentialClient(potential_client: PotentialClient) {
    this.router.navigate(['/employees', 'comercial', 'potential-clients', potential_client.id]);
    this.dialogRef.close();
  }

  findSuggestionIcon(route: any) {
    route = this.intranetRoutes.find(r => r.name === route.name);
    if (route) {
      return route.icon;
    } else return 'laptop_mac';
  }
}
